import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { LenderApproval, CompareApprovalsTab, ApprovalBadge, GqlCreditSubmissionApproval } from '../types';
import { COMPARISON_ROWS } from '../constants';
import { computeBadges, computeConditionsBadges, mapCreditSubmissionToApproval } from '../lib';
import { GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID } from '../queries';
import { EXAMPLE_APPROVAL } from '../lib/mockData';

export const useCompareApprovals = (dynamicsVoId?: string) => {
  const [activeTab, setActiveTab] = useState<CompareApprovalsTab>('approvals');

  const { data, loading, error, refetch } = useQuery(GET_APPROVALS_BY_VENDOR_OPPORTUNITY_ID, {
    variables: { voId: dynamicsVoId },
    skip: !dynamicsVoId,
  });

  const rawCreditSubmissions = useMemo<GqlCreditSubmissionApproval[]>(
    () =>
      (data?.creditSubmissionsByDynamicsVOId ?? []).filter(
        (cs: any): cs is GqlCreditSubmissionApproval => cs != null && cs.creditDecisionParsedData?.parsedData != null
      ),
    [data]
  );

  const approvals = useMemo<LenderApproval[]>(() => {
    const mapped = rawCreditSubmissions.map(mapCreditSubmissionToApproval);

    // Show the example card only when there is exactly 1 real approval.
    return mapped.length === 1 ? [...mapped, EXAMPLE_APPROVAL] : mapped;
  }, [data]);

  const badgesByRow = useMemo(() => {
    // Badges are only meaningful for real approvals – exclude the example card.
    const realApprovals = approvals.filter(a => !a.isExample);
    const badges: Record<string, Record<string, ApprovalBadge>> = {};

    COMPARISON_ROWS.forEach(row => {
      if (row.key === 'conditions') {
        badges[row.key] = computeConditionsBadges(realApprovals);
      } else {
        badges[row.key] = computeBadges(realApprovals, row);
      }
    });

    return badges;
  }, [approvals]);

  // Auto-switch to prescreen when there are no approvals to show.
  useEffect(() => {
    if (approvals.length === 0) {
      setActiveTab('prescreen');
    }
  }, [approvals.length]);

  return {
    activeTab,
    setActiveTab,
    approvals,
    rawCreditSubmissions,
    badgesByRow,
    loading,
    error,
    refetch,
  };
};
